import { Link, Navigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Check, ExternalLink, Navigation, Phone, Plus, Stamp } from 'lucide-react';
import { api } from '../lib/api';
import { DAYS, directionsUrl, place, scoreLabel, today } from '../lib/format';
import { isBar, kindLabel } from '../lib/places';
import { useTrip } from '../state/trip';
import PlaceMap from '../components/PlaceMap';
import PlaceCard from '../components/PlaceCard';
import KindChip from '../components/KindChip';
import { ErrorNote, ScoreBadge, Spinner } from '../components/ui';
import type { PlaceSummary } from '../lib/types';

/** Old /bars/:slug links (shared before the rename) land on the place's page. */
export function LegacyBarRedirect() {
  const { slug = '' } = useParams();
  return <Navigate to={`/stops/${encodeURIComponent(slug)}`} replace />;
}

export default function PlacePage() {
  const { slug = '' } = useParams();
  const { has, toggle, stamps, stamp, unstamp } = useTrip();
  const query = useQuery({ queryKey: ['place', slug], queryFn: () => api.place(slug) });

  if (query.isLoading) return <div className="mx-auto max-w-7xl px-4 sm:px-6"><Spinner label="Loading" /></div>;
  if (query.error || !query.data)
    return (
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <ErrorNote error={query.error}>We couldn&apos;t find that place.</ErrorNote>
      </div>
    );

  const b = query.data;
  const summary: PlaceSummary = { ...b, distanceMiles: null };
  const inTrip = has(b.id);
  const stamped = !!stamps[b.id];
  const day = today();

  return (
    <div className="mx-auto max-w-7xl px-4 pb-14 pt-10 sm:px-6">
      <Link to="/explore" className="text-sm">
        ← Explore
      </Link>

      <div className="mt-3 grid gap-8 lg:grid-cols-[1fr_440px]">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <p className="eyebrow">{[kindLabel(b.kind), place(b)].filter(Boolean).join(' · ')}</p>
            <h1 className="text-5xl font-black sm:text-6xl">{b.name}</h1>
            <div className="flex flex-wrap gap-2">
              <KindChip kind={b.kind} />
              <ScoreBadge score={b.backroadScore} curated={b.isCurated} />
              {!b.isCurated && <span className="chip">{scoreLabel(b.backroadScore)}</span>}
              {b.warning && <span className="chip chip-warn">{b.warning}</span>}
              {b.tags.map((t) => (
                <span key={t} className={`chip ${t === 'Cash only' ? 'chip-warn' : ''}`}>
                  {t}
                </span>
              ))}
            </div>
            {b.note && <p className="max-w-2xl text-lg text-[#d6d2c2]">{b.note}</p>}
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" className={`btn ${inTrip ? '' : 'btn-primary'}`} onClick={() => toggle(summary)} aria-pressed={inTrip}>
              {inTrip ? <Check size={18} aria-hidden /> : <Plus size={18} aria-hidden />}
              {inTrip ? 'In my trip' : 'Add to my trip'}
            </button>
            <button type="button" className="btn" onClick={() => (stamped ? unstamp(b.id) : stamp(summary))} aria-pressed={stamped}>
              <Stamp size={18} aria-hidden /> {stamped ? 'Stamped' : 'Stamp passport'}
            </button>
            <a className="btn no-underline" href={directionsUrl(b)} target="_blank" rel="noreferrer">
              <Navigation size={18} aria-hidden /> Directions
            </a>
            {b.phone && (
              <a className="btn no-underline" href={`tel:${b.phone.replace(/[^\d+]/g, '')}`}>
                <Phone size={18} aria-hidden /> {b.phone}
              </a>
            )}
            {b.website && (
              <a className="btn no-underline" href={b.website.startsWith('http') ? b.website : `https://${b.website}`} target="_blank" rel="noreferrer">
                <ExternalLink size={18} aria-hidden /> Website
              </a>
            )}
          </div>

          <section aria-labelledby="hoursH" className="card p-5">
            <h2 id="hoursH" className="mb-3 text-2xl font-black">Hours</h2>
            {b.weeklyHours ? (
              <div className="grid grid-cols-7 gap-1 text-center text-[13px]">
                {b.weeklyHours.map((h, i) => (
                  <div key={DAYS[i]} className={`rounded-md bg-asphalt px-1 py-2 tabular-nums ${i === day ? 'outline-2 outline-amber' : ''} ${h ? '' : 'text-dim'}`}>
                    <b className="block text-[11px] tracking-[0.08em] text-muted">{DAYS[i]}</b>
                    {h ?? 'Closed'}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted">
                We don&apos;t have hours for this one{b.openingHoursRaw ? ` (listed as “${b.openingHoursRaw}”)` : ''}. Call ahead.
              </p>
            )}
            <p className="mt-3 text-[13px] text-dim">Hours come from public listings and can change.</p>
          </section>

          <section className="card p-5">
            <h2 className="mb-2 text-2xl font-black">Getting there</h2>
            <p className="text-[#d6d2c2]">{[b.address, b.town, b.state, b.postalCode].filter(Boolean).join(', ') || 'Address not listed'}</p>
            {b.onRoutes.length > 0 && (
              <p className="mt-2">
                On route:{' '}
                {b.onRoutes.map((r, i) => (
                  <span key={r.slug}>
                    {i > 0 && ', '}
                    <Link to={`/routes/${r.slug}`}>{r.name}</Link>
                  </span>
                ))}
              </p>
            )}
            <p className="mt-2 text-[13px] text-dim">
              Source: {b.source === 'OpenStreetMap' ? 'OpenStreetMap' : b.source === 'Curated' ? 'Hand-picked by Backroad Stop' : 'Visitor submission'}.{' '}
              <Link to={`/owners?place=${encodeURIComponent(b.name)}&town=${encodeURIComponent(b.town ?? '')}&state=${b.state ?? ''}&id=${b.id}`}>
                Own this {isBar(b.kind) ? 'bar' : 'place'}? Claim it.
              </Link>
            </p>
          </section>
        </div>

        <div className="flex flex-col gap-4">
          <PlaceMap
            stops={[summary]}
            places={b.nearby}
            fitPoints={[[b.lat, b.lng], ...b.nearby.map((n) => [n.lat, n.lng] as [number, number])]}
            fitKey={b.slug}
            className="h-[340px]"
            label={`Map of ${b.name} and places nearby`}
          />
          {b.nearby.length > 0 && (
            <>
              <h2 className="text-2xl font-black">Nearby bars &amp; bites</h2>
              {b.nearby.map((n) => (
                <PlaceCard key={n.id} place={n} compact />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
